import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TopNavbar from '../components/TopNavbar';
import MainHeader from '../components/MainHeader';
import Footer from '../components/Footer';
import CircularLoader from '../components/CircularLoader';
import InteractiveStarRating from '../components/InteractiveStarRating';
import ProductRatingSection from '../components/ProductRatingSection';
import { getAllItems, getItemDetails } from '../services/api';
import { useCart } from '../context/CartContext';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [similarItems, setSimilarItems] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);

  const productId = useMemo(() => {
    if (id == null) return null;
    const n = Number(id);
    if (Number.isFinite(n) && n > 0) return n;
    return id;
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getItemDetails(productId);
        if (cancelled) return;

        const rawPrice = parseFloat(data?.price);
        const price = Number.isNaN(rawPrice) ? 0 : rawPrice;

        const image =
          data?.photo ||
          data?.image ||
          (Array.isArray(data?.images) && (data.images[0]?.image || data.images[0]?.url || data.images[0]?.src || data.images[0])) ||
          '';

        const categories = Array.isArray(data?.categories)
          ? data.categories
          : (typeof data?.category === 'string' && data.category.trim() ? [data.category] : []);

        setProduct({
          id: data?.id ?? productId,
          name: data?.name || 'Product',
          description: data?.description || '',
          price,
          rating: data?.average_rating || data?.rating || 0,
          reviews: data?.reviews_count || data?.reviews || 0,
          image,
          categories,
        });
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load product details');
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const slugify = (text) =>
    (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  useEffect(() => {
    if (!product?.id) return;

    let cancelled = false;
    const loadSimilar = async () => {
      try {
        setSimilarLoading(true);
        const res = await getAllItems();
        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);

        const primaryCategory =
          (Array.isArray(product?.categories) && product.categories.find((c) => String(c || '').trim())) ||
          '';

        const normalizePhotoValue = (value) => {
          if (!value || typeof value !== 'string') return '';
          const trimmed = value.trim();
          if (trimmed.startsWith('data:image')) return trimmed;
          if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
            return trimmed.replace(/^image\/upload\//, '');
          }
          return trimmed;
        };

        const mapped = items
          .map((it) => {
            const rawPrice = parseFloat(it?.price);
            const price = Number.isNaN(rawPrice) ? 0 : rawPrice;

            let image = '';
            const candidateSingleRaw = it?.photo || it?.image;
            const candidateSingle = normalizePhotoValue(candidateSingleRaw);
            const candidateArrayRaw = it?.images || [];
            const candidateArray = Array.isArray(candidateArrayRaw)
              ? candidateArrayRaw
                  .map((img) => {
                    if (!img) return '';
                    if (typeof img === 'string') return normalizePhotoValue(img);
                    if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
                    return '';
                  })
                  .filter(Boolean)
              : [];

            if (candidateSingle && typeof candidateSingle === 'string' && candidateSingle.trim() !== '') {
              image = candidateSingle;
            } else if (Array.isArray(candidateArray) && candidateArray.length > 0) {
              const thumbObj = Array.isArray(candidateArrayRaw)
                ? candidateArrayRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail)
                : null;
              if (thumbObj && typeof thumbObj.image === 'string') {
                image = normalizePhotoValue(thumbObj.image);
              } else {
                image = candidateArray[0] || '';
              }
            }

            return {
              id: it?.id,
              name: it?.name,
              price,
              image,
              category: it?.category || '',
            };
          })
          .filter((x) => x?.id && x?.name && x?.image);

        const filtered = mapped
          .filter((x) => String(x.id) !== String(product.id))
          .filter((x) => {
            if (!primaryCategory) return true;
            return String(x.category || '').trim().toLowerCase() === String(primaryCategory).trim().toLowerCase();
          })
          .slice(0, 4);

        if (!cancelled) setSimilarItems(filtered);
      } catch (_) {
        if (!cancelled) setSimilarItems([]);
      } finally {
        if (!cancelled) setSimilarLoading(false);
      }
    };

    loadSimilar();
    return () => {
      cancelled = true;
    };
  }, [product?.id, product?.categories]);

  const handleAdd = () => {
    if (!product) return;
    for (let i = 0; i < (Number(quantity) || 1); i++) {
      addToCart(product);
    }
  };

  return (
    <div className="App bg-white">
      <TopNavbar />
      <MainHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="min-w-0">
            <nav className="text-sm text-gray-500 mb-2">
              <Link to="/" className="hover:text-gray-900">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/shop" className="hover:text-gray-900">Shop</Link>
              {product?.name && (
                <>
                  <span className="mx-2">/</span>
                  <span className="text-gray-900 font-semibold truncate">{product.name}</span>
                </>
              )}
            </nav>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
              {product?.name || 'Product Details'}
            </h1>
          </div>

          <button
            type="button"
            onClick={() => navigate('/shop')}
            className="shrink-0 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border border-gray-300 text-gray-900 bg-white hover:bg-gray-50 shadow-sm"
          >
            <span aria-hidden="true">←</span>
            Back to product items
          </button>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-10">
            <CircularLoader label="Loading product..." size="lg" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <div className="text-red-700 font-semibold">{error}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-3xl bg-gray-50 p-6">
              <img
                src={product?.image}
                alt={product?.name}
                className="w-full h-[420px] object-cover rounded-2xl"
                loading="lazy"
              />
            </div>

            <div className="rounded-3xl bg-white border border-gray-200 p-6 sm:p-8">
              <InteractiveStarRating
                itemId={product?.id}
                currentRating={product?.rating}
                reviews={product?.reviews}
                showReviews={true}
                size="lg"
              />

              <div className="flex items-center space-x-2 mt-4 mb-6">
                <span className="text-3xl font-bold text-orange-500">KES {Number(product?.price || 0).toLocaleString()}</span>
              </div>

              {product?.description && (
                <p className="text-gray-600 leading-relaxed mb-8">{product.description}</p>
              )}

              <div className="mb-8">
                <ProductRatingSection
                  itemId={product?.id}
                  currentRating={product?.rating}
                  reviews={product?.reviews}
                />
              </div>

              <div className="flex items-center gap-3 mb-8">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <span className="text-lg">−</span>
                </button>
                <span className="text-lg font-semibold w-8 text-center">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <span className="text-lg">+</span>
                </button>

                <button
                  type="button"
                  onClick={handleAdd}
                  className="ml-2 inline-flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 py-3 font-semibold transition-colors"
                >
                  Add To Cart
                </button>
              </div>

              {Array.isArray(product?.categories) && product.categories.length > 0 && (
                <div className="rounded-2xl bg-gray-50 border border-gray-200 p-5">
                  <div className="text-sm font-semibold text-gray-900 mb-1">Categories</div>
                  <div className="text-sm text-gray-700">{product.categories.join(', ')}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="mt-12">
            <div className="flex items-center justify-between gap-4 mb-5">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Similar Items</h2>
              <Link to="/shop" className="text-sm font-semibold text-orange-600 hover:text-orange-700">
                View all
              </Link>
            </div>

            {similarLoading ? (
              <div className="rounded-xl border border-gray-200 bg-white p-8">
                <CircularLoader label="Loading similar items..." size="md" />
              </div>
            ) : similarItems.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600">
                No similar items available.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {similarItems.map((it) => (
                  <Link
                    key={it.id}
                    to={`/product/${encodeURIComponent(it.id)}/${encodeURIComponent(slugify(it.name))}`}
                    className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="relative">
                      <img
                        src={it.image}
                        alt={it.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="font-semibold text-gray-900 line-clamp-2">{it.name}</div>
                      <div className="mt-2 text-orange-600 font-bold">KES {Number(it.price || 0).toLocaleString()}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetails;
